import { action }                from 'mobx';
import { computed }              from 'mobx';
import { makeObservable }        from 'mobx';
import { observable }            from 'mobx';
import { runInAction }           from 'mobx';
import ApiCollection             from '@widesk-core/models/ApiCollection';
import ThemeModel                from '@models/map/ThemeModel';
import { ThemeConfiguration }    from '@models/map/ThemeModel';
import { ThemeScope }            from '@models/map/ThemeModel';
import { ThemeStyle }            from '@models/map/ThemeModel';
import { ThemeTextStyle }        from '@models/map/ThemeModel';
import { SaveState }             from '@/TokamapEditor/ux/SaveIndicator';
import currentProjectEditorStore from '@/TokamapEditor/stores/currentProjectEditorStore';
import nextFreeLabel             from '@/tools/nextFreeLabel';
import { ShapeTheme }            from '@tokamap/core';
import { ShapeThemes }           from '@tokamap/core';
import { ShapeTextStyle }        from '@tokamap/core';

export type { ThemeScope, ThemeStyle, ThemeTextStyle };

/** Le `ThemeModel` aplati. `default` n'existe pas sur la ressource : côté API c'est le PROJET qui
 * désigne son thème par défaut (`Project.defaultTheme`), reporté ici sur chaque thème. */
export type Theme = {
	id: string;
	name: string;
	default: boolean;
	appearance: Record<ThemeScope, ThemeStyle>;
	text: Record<ThemeScope, ThemeTextStyle>;
	height?: number;
	elevation?: number;
	hoverHeight?: number;
	hoverElevation?: number;
	minZoomOffset?: number;
};

/**
 * Les thèmes du projet (`GET /themes?project=`), gardés en ENTIER en mémoire : la carte a besoin de
 * tous pour dessiner les formes liées, d'où l'absence de pagination.
 */
class ThemeStore {
	@observable.ref themes: Theme[] = [];

	@observable isLoaded = false;

	/** Porté par le store et non par le formulaire : cet écran n'a pas de bouton d'enregistrement,
	 * toutes ses modifications passent par `_write`. */
	@observable saveState: SaveState = 'idle';

	private _savedTimeout?: ReturnType<typeof setTimeout>;

	/** Le modèle derrière chaque `Theme`, indexé par id : c'est lui qui porte les `PATCH`. */
	private _models = new Map<string, ThemeModel>();

	private _queue: Promise<unknown> = Promise.resolve();

	constructor() {
		makeObservable(this);
	}

	public async load() {
		const project = currentProjectEditorStore.get.project;
		const collection = new ApiCollection(ThemeModel);

		// `_defaultThemeId` se lit sur le projet, que `load` peut précéder.
		await project.whenIsLoaded();

		// Sans `itemsPerPage`, le connecteur enchaîne les pages jusqu'à tout avoir.
		await collection.setFilter('project', project.id).list();

		runInAction(() => {
			this._models = new Map(collection.models.map(model => [String(model.id), model]));
			this.themes = collection.models.map(model => toTheme(model, this._defaultThemeId));
			this.isLoaded = true;
		});

		return this.themes;
	}

	/** Le POST précède l'ajout à la liste : l'id vient de l'API, et c'est lui qui identifie le thème
	 * partout ailleurs (`Shape.themeId`). */
	public async addTheme() {
		const project = currentProjectEditorStore.get.project;
		const name = nextFreeLabel('Nouveau thème', this.themes.map(theme => theme.name));

		const model = new ThemeModel();

		await this._write(() => model.patch({
			label: name,
			configuration: emptyConfiguration(),
			project: project.urn,
		}));

		const theme = toTheme(model, this._defaultThemeId);

		runInAction(() => {
			this._models.set(theme.id, model);
			this.themes = [...this.themes, theme];
		});

		return theme;
	}

	public async removeTheme(id: string) {
		const model = this._models.get(id);

		if (!model) throw new Error(`Thème introuvable.`);

		await this._write(() => model.destroy());

		runInAction(() => {
			this._models.delete(id);
			this.themes = this.themes.filter(theme => theme.id !== id);
		});
	}

	public async renameTheme(id: string, name: string) {
		return this._patchTheme(id, theme => ({ ...theme, name }), { label: name });
	}

	public async setThemeStyle(id: string, scope: ThemeScope, style: ThemeStyle) {
		return this._patchTheme(id, theme => ({
			...theme,
			appearance: { ...theme.appearance, [scope]: { ...theme.appearance[scope], ...style } },
		}));
	}

	public async setThemeText(id: string, scope: ThemeScope, style: ThemeTextStyle) {
		return this._patchTheme(id, theme => ({
			...theme,
			text: { ...theme.text, [scope]: { ...theme.text[scope], ...style } },
		}));
	}

	public async setThemeHeight(id: string, height: number | undefined) {
		return this._patchTheme(id, theme => ({ ...theme, height }));
	}

	public async setThemeElevation(id: string, elevation: number | undefined) {
		return this._patchTheme(id, theme => ({ ...theme, elevation }));
	}

	/** `undefined` reprend celles de l'état normal. */
	public async setThemeHoverHeights(id: string, heights: { hoverHeight?: number; hoverElevation?: number; }) {
		return this._patchTheme(id, theme => ({ ...theme, ...heights }));
	}

	public async setThemeMinZoomOffset(id: string, minZoomOffset: number | undefined) {
		return this._patchTheme(id, theme => ({ ...theme, minZoomOffset }));
	}

	/** `undefined` retire le thème par défaut. Un seul `PATCH`, sur le PROJET, qui n'accepte qu'un
	 * thème : le drapeau de chaque `Theme` n'est qu'un reflet local. */
	public async setDefaultTheme(id: string | undefined) {
		const project = currentProjectEditorStore.get.project;
		const urn = id ? this._models.get(id)?.urn : undefined;

		if (id && !urn) throw new Error(`Thème introuvable.`);

		const previous = this.themes;

		this._setThemes(this.themes.map(theme => ({ ...theme, default: theme.id === id })));

		try {
			await this._write(() => project.patch({ defaultTheme: urn ?? null }));
		} catch (err) {
			this._setThemes(previous);
			throw err;
		}
	}

	public getTheme(id: string) {
		return this.themes.find(theme => theme.id === id);
	}

	/** Thème appliqué aux formes nouvellement créées, s'il y en a un. */
	@computed get defaultTheme(): Theme | undefined {
		return this.themes.find(theme => theme.default);
	}

	/** La table poussée dans l'instance pour redessiner les formes portant un `Shape.themeId` — voir
	 * `TokaMapInstance.setThemes`. */
	@computed get coreThemes(): ShapeThemes {
		return Object.fromEntries(this.themes.map(theme => [theme.id, toCoreTheme(theme)]));
	}

	private get _defaultThemeId() {
		const id = currentProjectEditorStore.get.project.getResolvableId('defaultTheme');

		return id === undefined ? undefined : String(id);
	}

	/** Applique la modification localement, puis l'enregistre — `attributes` complète la configuration,
	 * toujours déduite du thème mis à jour. */
	private async _patchTheme(
		id: string,
		update: (theme: Theme) => Theme,
		attributes: Record<string, unknown> = {},
	) {
		const model = this._models.get(id);
		const current = this.getTheme(id);

		if (!model || !current) throw new Error(`Thème introuvable.`);

		const previous = this.themes;
		const updated = update(current);

		this._setThemes(this.themes.map(theme => theme.id === id ? updated : theme));

		try {
			await this._write(() => model.patch({ ...attributes, configuration: toConfiguration(updated) }));
		} catch (err) {
			this._setThemes(previous);
			throw err;
		}
	}

	/** Enfile l'écriture et tient l'indicateur d'enregistrement à jour. */
	private async _write<T>(task: () => Promise<T>): Promise<T> {
		clearTimeout(this._savedTimeout);
		this._setSaveState('saving');

		try {
			const result = await this._enqueue(task);

			this._setSaveState('saved');
			this._savedTimeout = setTimeout(() => this._setSaveState('idle'), 2500);

			return result;
		} catch (err) {
			this._setSaveState('error');
			throw err;
		}
	}

	@action private _setSaveState(value: SaveState) {
		this.saveState = value;
	}

	/** Sérialise les écritures, sans qu'un échec bloque la file pour les suivantes. */
	private _enqueue<T>(task: () => Promise<T>): Promise<T> {
		const result = this._queue.then(task, task);

		this._queue = result.catch(() => undefined);

		return result;
	}

	@action private _setThemes(themes: Theme[]) {
		this.themes = themes;
	}
}

/** Couleur de repli des deux champs que le schéma de l'API exige — voir `ThemeConfiguration`. */
const FALLBACK_COLOR = '#000000';

const emptyConfiguration = (): ThemeConfiguration => ({
	bgColor: FALLBACK_COLOR,
	borderColor: FALLBACK_COLOR,
	appearance: { normal: {}, hover: {} },
	text: { normal: {}, hover: {} },
});

const toConfiguration = (theme: Theme): ThemeConfiguration => ({
	bgColor: theme.appearance.normal.bgColor || FALLBACK_COLOR,
	borderColor: theme.appearance.normal.borderColor || FALLBACK_COLOR,
	appearance: theme.appearance,
	text: theme.text,
	height: theme.height ?? null,
	elevation: theme.elevation ?? null,
	hoverHeight: theme.hoverHeight ?? null,
	hoverElevation: theme.hoverElevation ?? null,
	minZoomOffset: theme.minZoomOffset ?? null,
});

/** Satisfait aussi bien par un `ThemeModel` que par le JSON brut de l'API. */
type RawTheme = { id?: unknown; label?: unknown; configuration?: unknown };

function toCoreTheme(theme: Theme): ShapeTheme {
	return {
		style: orUndefined(theme.appearance.normal),
		hoverStyle: orUndefined(theme.appearance.hover),
		text: orUndefined(toCoreTextStyle(theme.text.normal)),
		hoverText: orUndefined(toCoreTextStyle(theme.text.hover)),
		...(theme.height ? { height: theme.height } : {}),
		...(theme.elevation ? { elevation: theme.elevation } : {}),
		...(theme.hoverHeight ? { hoverHeight: theme.hoverHeight } : {}),
		...(theme.hoverElevation ? { hoverElevation: theme.hoverElevation } : {}),
		...(theme.minZoomOffset ? { minZoomOffset: theme.minZoomOffset } : {}),
	};
}

function toCoreTextStyle({ fixedSizePx, ...colors }: ThemeTextStyle): ShapeTextStyle {
	return fixedSizePx ? { ...colors, fixedSizePx } : colors;
}

/** L'API ne valide pas `configuration` : les replis gardent `theme.appearance[scope]` et
 * `theme.text[scope]` sûrs à la lecture. */
function toTheme(raw: RawTheme, defaultThemeId?: string): Theme {
	const id = String(raw.id ?? '');
	const configuration = raw.configuration as Partial<ThemeConfiguration> | undefined;

	return {
		id,
		name: String(raw.label ?? ''),
		default: id === defaultThemeId,
		appearance: {
			normal: configuration?.appearance?.normal || {},
			hover: configuration?.appearance?.hover || {},
		},
		text: {
			normal: configuration?.text?.normal || {},
			hover: configuration?.text?.hover || {},
		},
		...(typeof configuration?.height === 'number' && configuration.height > 0 ? { height: configuration.height } : {}),
		...(typeof configuration?.elevation === 'number' && configuration.elevation > 0 ? { elevation: configuration.elevation } : {}),
		...(typeof configuration?.hoverHeight === 'number' && configuration.hoverHeight > 0 ? { hoverHeight: configuration.hoverHeight } : {}),
		...(typeof configuration?.hoverElevation === 'number' && configuration.hoverElevation > 0 ? { hoverElevation: configuration.hoverElevation } : {}),
		...(typeof configuration?.minZoomOffset === 'number' && configuration.minZoomOffset > 0 ? { minZoomOffset: configuration.minZoomOffset } : {}),
	};
}

/**
 * `{}` en interne (pratique : `theme.text[scope]` se lit sans garde) devient `undefined` à la
 * frontière du core, qui distingue les deux — voir `ShapeTheme.hoverText` : ABSENT signifie "hérite
 * de l'état normal", alors qu'un objet PRÉSENT sans couleur signifie "aucune couleur", ce qui fait
 * retomber le rendu sur les défauts de l'adapter.
 *
 * Un champ explicitement vidé (le sélecteur de couleur en `allowClear` écrit `undefined`) compte
 * comme non défini : sinon le simple fait d'avoir ouvert puis vidé un sélecteur suffirait à couper
 * l'héritage.
 */
function orUndefined<T extends object>(value: T): T | undefined {
	return Object.values(value).some(v => v !== undefined) ? value : undefined;
}

export default new ThemeStore();
